import {usePost} from '@app/blog/post/use-post';
import React, {Fragment, Suspense, useEffect, useState} from 'react';
import {PageStatus} from '@common/http/page-status';
import {CustomPost} from '@app/blog/post/custom-post';
import {FormProvider, useForm} from 'react-hook-form';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {ArticleEditorTitle} from '@common/article-editor/article-editor-title';
import {ArticleEditorStickyHeader} from '@common/article-editor/article-editor-sticky-header';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {CreatePostPayload} from '@app/blog/post/requests/use-create-post';
import {useUpdatePost} from '@app/blog/post/requests/use-update-post';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import { useParams } from 'react-router-dom';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {Trans} from '@common/i18n/trans';
import {Footer} from '@common/ui/footer/footer';
import {DashboardNavbar} from '@app/dashboard/dashboard-navbar';
import {FormTextField} from '@common/ui/forms/input-field/text-field/text-field';
import {FormSelect, Option} from '@common/ui/forms/select/select';
import {Form} from '@common/ui/forms/form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {Button} from '@common/ui/buttons/button';
import {FormImageSelector} from '@common/ui/images/image-selector';
import {FormNormalizedModelField} from '@common/ui/forms/normalized-model-field';
import {RichTextEditor} from '@common/ui/forms/richtexteditor';
import {queryClient} from '@common/http/query-client';
import {BlogSidebar} from '@app/blog/blog-sidebar';
import {apiClient} from '@common/http/query-client';
import {Item} from '@common/ui/forms/listbox/item';
import Select from 'react-select';

export function EditPost() {
  const params = useParams();
  const query = usePost(params.post_id, params.site_id);

  
  return query.data ? (
    <Fragment>     
      <PageContent page={query.data as CustomPost} />
    </Fragment>
  ) : (
    <div className="relative w-full h-full">
      <PageStatus query={query} />
    </div>
  );
}

interface PostContentProps {
  page: CustomPost;
}
function PageContent({page}: PostContentProps) {
  const navigate = useNavigate();
  const crupdatePost = useUpdatePost();
  const { site_id } = useParams();
  const form = useForm<CreatePostPayload>({
    defaultValues: {
      title: page.title,     
      content: page.content || '',
      featured_image: page.featured_image,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
      meta_keywords: page.meta_keywords,
      excerpt: page.excerpt,
      status: page.status,
      categories: (page.categories || []).map((cat: any) => ({
        value: cat.id,
        label: cat.name,
      })),
    },
  });

  const { setValue, register, handleSubmit } = form;  
  
  const [availableCategories, setAvailableCategories] = React.useState([]);
  
  // Register 'content' with react-hook-form
  useEffect(() => {
    form.register('project_id'); // Register `project_id`
    form.setValue('project_id', site_id ? parseInt(site_id, 10) : undefined); 
    apiClient.get(`categories/${site_id}?all=true`).then((response) => {
      const categories = response.data[0];
      setAvailableCategories(categories.map((cat: any) => ({ value: cat.id, label: cat.name })));
    });
  }, [form, site_id]);

  return (
    <Fragment>      
      <StaticPageTitle>
        <Trans message="Update Post" />
      </StaticPageTitle>
      <DashboardNavbar />
      <div className="flex h-full">
        {/* Sidebar Menu */}
        <BlogSidebar />
        {/* Main Content */}
        <div className="flex-1 p-6">
         <div className="p-12 md:p-24">
          <div className="mb-16 flex items-center justify-between">
            <h1 className="text-3xl font-light first:capitalize" id=":r3r:">Update Post</h1> 
            <Button
              variant="flat"
                    color="primary"
              onClick={() => {
                const formData = form.getValues();
                const previewUrl = `sites/${site_id}/blog/preview`;

                fetch(previewUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(formData),
                })
                .then(response => response.text())
                .then(html => {
                  const newWindow = window.open('', '_blank');

                  if (newWindow) {
                    newWindow.document.open();
                    newWindow.document.write(html);
                    newWindow.document.close();
                  }
                });
              }}
            >
              Preview
            </Button>

          </div>
          <Form
          id="create-post"
          form={form}
          onSubmit={value => {
            const payload = { ...value, categories: (value.categories || []).map((cat: any) => cat.value), project_id: site_id };
            crupdatePost.mutate(payload, {
              onSuccess: response => {
                queryClient.invalidateQueries({queryKey:['posts', site_id],});
                navigate(`/${site_id}/posts`, {
                  replace: true,
                });
              },
            });
          }}
        >
          <Fragment>
            <FormTextField
              autoFocus
              name="title"
              label={<Trans message="Title" />}
              className="mb-14"
              required
            />
            
            <label htmlFor="categories">Categories</label>
           
      <Select
        isMulti
        name="categories"
        options={availableCategories}
        value={form.watch('categories')}
        onChange={(selected) => {
          // Cast or map `selected` to the expected mutable array
          setValue(
            'categories',
            (selected as { value: number; label: string }[]) || []
          );
        }}
        required
      />  
        <FormSelect className="mt-14" name="status" label="Status" selectionMode="single" required>
  <Item value="draft">Draft</Item>
  <Item value="published">Published</Item>
</FormSelect>    
            
            <div className="mt-14">
              <label>Content </label>

              <RichTextEditor
                value={form.watch('content') || ''}
                onChange={(value) => form.setValue('content', value)} // Update `content`
              />
            </div>

            <div className="mt-14">
              <FileUploadProvider>
                <FormImageSelector
                  name="featured_image"
                  diskPrefix="Blogs"
                  variant="square"
                  stretchPreview
                  label={<Trans message="Featured Image" />}
                  previewSize="w-100 h-100"
                  showRemoveButton
                />
              </FileUploadProvider>
            </div>
            <FormTextField
              
              name="meta_title"
              label={<Trans message="Meta Title" />}
              className="mb-14 mt-14"
              required
            />
            <FormTextField
              
              name="meta_description"
              label={<Trans message="Meta Description" />}
              className="mb-14"
              required
            />
            <FormTextField                
                name="excerpt"
                label={<Trans message="Excerpt" />}
                className=""
                inputElementType="textarea"
                
              />
            <div className="text-muted">Max 200 charcters</div>
            <div className="mt-14">
              <Button onClick={() => navigate(`/${site_id}/posts`)}>
                <Trans message="Cancel" />
              </Button>
              <Button
                form="create-post"
                type="submit"
                variant="flat"
                color="primary"
                disabled={crupdatePost.isPending}
              >
                <Trans message="Update" />
              </Button>
            </div>
          </Fragment>
        </Form>
      </div>
        </div>
      </div>
        
      <Footer className="mt-40 px-40" />        
    </Fragment>
  );
}
